/**
 * Shared auth-state accessor — thin adapter over TanStack Query.
 *
 * Historically this module hand-rolled dedup/TTL for `/api/auth/me`. That job now
 * belongs to the shared `currentUser` query (see hooks/queries/useCurrentUser).
 * This adapter keeps the old imperative API (`fetchCurrentUser` / `invalidateAuthCache`)
 * so existing non-hook call sites keep working while they migrate to `useCurrentUser`,
 * and every consumer — hook or imperative — shares one cache entry.
 *
 *   const user = await fetchCurrentUser();     // returns user or null
 *   invalidateAuthCache();                     // after login/logout
 */

import { queryClient } from '@/lib/queryClient';
import { queryKeys } from '@/api/queryKeys';
import { fetchMe } from '@/api/auth';

export async function fetchCurrentUser(opts = {}) {
  return queryClient.fetchQuery({
    queryKey: queryKeys.currentUser,
    queryFn: fetchMe,
    // force=true bypasses cache; otherwise reuse a value fetched within 10s
    // (matches the old TTL that coalesced a page's mount-time calls).
    staleTime: opts.force ? 0 : 10_000,
  });
}

export function invalidateAuthCache() {
  queryClient.invalidateQueries({ queryKey: queryKeys.currentUser });
}
